import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Fx, Stack } from 'Components';
import { useStores } from 'Hooks/useStore';
import { DashboardContext } from 'Pages/Dashboard/DashboardContext';
import DashboardFilters from 'Pages/Dashboard/DashboardFilters/DashboardFilters';
import { DashboardFiltersValues } from 'Pages/Dashboard/Dashboard.types';
import { getAppRoutes } from 'Pages/App/App.constants';
import HeaderRow from './components/HeaderRow';

function TenantProvider({ children, showHeader }: { children: React.ReactNode, showHeader: boolean }) {
    const stores = useStores();
	const { authStore, systemsStore } = stores;
    const isSuperAdmin = authStore.isCurrentUserSuperAdmin;
    const [filterValues, setFilterValues] = useState<DashboardFiltersValues>({});
    const { pathname } = useLocation();
    const [hasProperties, setHasProperties] = useState(true);
    const appRoutes = getAppRoutes();

    const onDashboardFiltersChange = (value: DashboardFiltersValues) => {
        setFilterValues({ ...filterValues, ...value });
    };

    const componentDetails = useMemo(() => {
        let details = null;
        const normalizedPath = pathname === '/' ? pathname : pathname.replace(/\/+$/, '');
        if (appRoutes.USER_ACTIVITY === normalizedPath) {
            details = {
                title: "User Activity",
                subTitle: "Monitor user activity and account statistics.",
                showFilter: true
            }
        }
        else if (appRoutes.PHISHING === normalizedPath) {
            details = {
                title: "Phishing",
                subTitle: "Review phishing campaign reports.",
                showFilter: false
            }
        } else if (appRoutes.TRAINING === normalizedPath) {
            details = {
                title: "Training",
                subTitle: "Analyze training completion and progress.",
                showFilter: true
            }
        } else if (appRoutes.COURSES === normalizedPath) {
            details = {
                title: "Courses",
                subTitle: "A quick overview of your assigned training and progress.",
                showFilter: isSuperAdmin
            }
        } else if (appRoutes.REPORTS === normalizedPath) {
            details = {
                title: "Library",
                subTitle: "Browse available report templates.",
                showFilter: false
            }
        } else if (appRoutes.SAVED_REPORTS === normalizedPath) {
            details = {
                title: "Saved",
                subTitle: "Access your saved reports.",
                showFilter: false
            }
        } else if (appRoutes.HOME === normalizedPath) {
            details = {
                title: "Dashboard",
                subTitle: "Overview of metrics and performance.",
                showFilter: true
            }
        }
        return details;
    }, [pathname, isSuperAdmin])

    useEffect(() => {
		const tenantId = systemsStore.selectedTenant.value;
		if(!isSuperAdmin && tenantId && !filterValues.id) onDashboardFiltersChange({ id: tenantId });
	}, [systemsStore])

    useEffect(() => {
        return () => stores.clearDashboardStore();
    }, []);

    return (
        <DashboardContext.Provider value={{ filters: filterValues, hasProperties }}>
            <Fx column grow={1} style={{ display: showHeader ? "block" : "none" }}>
                <Stack>
                    <HeaderRow title={componentDetails?.title || ""} subHeading={componentDetails?.subTitle || ""}>
                        <DashboardFilters showFilter={componentDetails?.showFilter} onChange={onDashboardFiltersChange} onSetHasProperties={setHasProperties} />
                    </HeaderRow>
                    {showHeader ? children : null}
                </Stack>
            </Fx>
            {showHeader ? null : children}
        </DashboardContext.Provider>
    );
}

export default TenantProvider;